import { useSearchParams } from 'react-router-dom';
import type { ComponentType } from 'react';
import {
  MdDescription,
  MdLocalOffer,
  MdShoppingCart,
  MdSync,
  MdReportProblem,
  MdTrendingUp,
  MdArchive,
} from 'react-icons/md';
import { erpPageTabsClass, erpPageTabClasses } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';
import { RFQsPage } from '../RFQs/RFQsPage';
import { RFQDetailsPage } from '../RFQs/RFQDetailsPage';
import { OffersPage } from '../Offers/OffersPage';
import { CreateOfferPage } from '../Offers/CreateOfferPage';
import { OfferDetailsPage } from '../Offers/OfferDetailsPage';
import { POListPage } from '../PO/POListPage';
import { ClaimRequestsPage } from '../ClaimRequests/ClaimRequestsPage';
import {
  usePipelineOverviewCounts,
  type PipelineOverviewCounts,
} from '../../hooks/usePipelineOverviewCounts';
import { CompletedOrdersPage } from '../CompletedOrders/CompletedOrdersPage';

type SalesTab =
  | 'overview'
  | 'rfqs'
  | 'offers'
  | 'purchase-orders'
  | 'in-progress'
  | 'claims'
  | 'order-history';

const SALES_TAB_PARAM = 'salesTab';
const RFQ_ID_PARAM = 'rfqId';
const OFFER_VIEW_PARAM = 'offerView';
const OFFER_ID_PARAM = 'offerId';

const SALES_TABS = [
  { id: 'overview' as const, label: 'Overview', icon: MdTrendingUp },
  { id: 'rfqs' as const, label: 'RFQ', icon: MdDescription },
  { id: 'offers' as const, label: 'Offers', icon: MdLocalOffer },
  { id: 'purchase-orders' as const, label: 'Purchase Orders', icon: MdShoppingCart },
  { id: 'in-progress' as const, label: 'In Progress', icon: MdSync },
  { id: 'claims' as const, label: 'Claims', icon: MdReportProblem },
  { id: 'order-history' as const, label: 'Order History', icon: MdArchive },
];

const SALES_RFQ_LIST_PATH = '/dashboard/admin/sales?salesTab=rfqs';
const SALES_OFFERS_LIST_PATH = '/dashboard/admin/sales?salesTab=offers';

const COUNT_BY_TAB: Record<Exclude<SalesTab, 'overview'>, keyof PipelineOverviewCounts> = {
  rfqs: 'rfqs',
  offers: 'offers',
  'purchase-orders': 'purchaseOrders',
  'in-progress': 'inProgress',
  claims: 'claims',
  'order-history': 'completedOrders',
};

/** Map legacy query values so old bookmarks still open a sensible tab. */
function parseSalesTab(value: string | null): SalesTab {
  if (value === 'sales' || value === 'transit') return 'rfqs';
  if (value === 'claim-management') return 'claims';
  if (value === 'orders-delivered' || value === 'completed-orders') return 'order-history';
  if (value && SALES_TABS.some((tab) => tab.id === value)) {
    return value as SalesTab;
  }
  return 'overview';
}

function formatCount(value: number, loading: boolean): string {
  if (loading) return '—';
  return value.toLocaleString();
}

export function SalesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseSalesTab(searchParams.get(SALES_TAB_PARAM));
  const rfqId = searchParams.get(RFQ_ID_PARAM);
  const offerView = searchParams.get(OFFER_VIEW_PARAM);
  const offerId = searchParams.get(OFFER_ID_PARAM);
  const { counts, isLoading: countsLoading } = usePipelineOverviewCounts(
    'sales',
    activeTab === 'overview'
  );

  const setTab = (tab: SalesTab) => {
    const next = new URLSearchParams();
    if (tab !== 'overview') {
      next.set(SALES_TAB_PARAM, tab);
    }
    setSearchParams(next, { replace: true });
  };

  const showRfqDetail = activeTab === 'rfqs' && Boolean(rfqId);
  const showOfferCreate = activeTab === 'offers' && offerView === 'new';
  const showOfferEdit =
    activeTab === 'offers' && offerView === 'edit' && Boolean(offerId);
  const showOfferDetail =
    activeTab === 'offers' && offerView === 'detail' && Boolean(offerId);
  const showOffersList =
    activeTab === 'offers' && !showOfferCreate && !showOfferEdit && !showOfferDetail;

  return (
    <div className="w-full min-h-screen bg-[hsl(var(--background))] p-4 lg:p-8 font-sans text-[hsl(var(--foreground))]">
      <div className={cn(erpPageTabsClass, 'overflow-x-auto mb-6')}>
        {SALES_TABS.map((tab) => {
          const Icon = tab.icon as ComponentType<{ className?: string }>;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                erpPageTabClasses(isActive),
                'inline-flex items-center gap-2 whitespace-nowrap'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'rfqs' && showRfqDetail && (
        <RFQDetailsPage rfqId={rfqId!} embedded backTo={SALES_RFQ_LIST_PATH} />
      )}
      {activeTab === 'rfqs' && !showRfqDetail && <RFQsPage embedded pipeline="sales" />}
      {showOfferCreate && (
        <CreateOfferPage embedded backTo={SALES_OFFERS_LIST_PATH} />
      )}
      {showOfferEdit && (
        <CreateOfferPage
          embedded
          offerId={offerId!}
          backTo={SALES_OFFERS_LIST_PATH}
        />
      )}
      {showOfferDetail && (
        <OfferDetailsPage
          embedded
          offerId={offerId!}
          backTo={SALES_OFFERS_LIST_PATH}
        />
      )}
      {showOffersList && <OffersPage embedded pipeline="sales" />}
      {activeTab === 'purchase-orders' && (
        <POListPage embedded direction="received" statusScope="open" />
      )}
      {activeTab === 'in-progress' && (
        <POListPage embedded direction="received" statusScope="in-progress" />
      )}
      {activeTab === 'claims' && <ClaimRequestsPage embedded mode="received" />}
      {activeTab === 'order-history' && (
        <CompletedOrdersPage embedded pipeline="sales" />
      )}
      {activeTab === 'overview' && (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 lg:p-8 shadow-sm space-y-6 mb-6">
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Sales pipeline</h2>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
              Customer RFQ → your offer → purchase order → proforma → shipping → final invoice.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 mb-7">
            {(
              [
                {
                  tab: 'rfqs' as const,
                  title: 'RFQ',
                  desc: 'Receive customer RFQs (also sent to selected vendors).',
                },
                {
                  tab: 'offers' as const,
                  title: 'Offers',
                  desc: 'Send a special offer with payment & delivery terms.',
                },
                {
                  tab: 'purchase-orders' as const,
                  title: 'Purchase Orders',
                  desc: 'When the customer accepts, the PO lands here.',
                },
                {
                  tab: 'in-progress' as const,
                  title: 'In Progress',
                  desc: 'Send proforma → customer approves → shipping choice → final invoice.',
                },
                {
                  tab: 'claims' as const,
                  title: 'Claims',
                  desc: 'Post-delivery claim requests.',
                },
                {
                  tab: 'order-history' as const,
                  title: 'Order History',
                  desc: 'Closed POs with settled claims — historical analytics.',
                },
              ] as const
            ).map((item) => {
              const count = counts[COUNT_BY_TAB[item.tab]];
              return (
                <div
                  key={item.tab}
                  className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4 mb-4"
                >
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <span className="font-semibold text-[hsl(var(--foreground))]">
                      {item.title}
                    </span>
                    <span
                      className="shrink-0 text-2xl font-black tabular-nums leading-none text-[#0088cc]"
                      aria-label={`${item.title} count: ${formatCount(count, countsLoading)}`}
                    >
                      {formatCount(count, countsLoading)}
                    </span>
                  </div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
